<template>
  <div>
    <RevForm
      :id="formId"
      :initial-values="initialFormValues"
      :name="formId"
      :validate="validate()"
    >
      <template #default="{ values, errors }">
        <div class="space-y-36">
          <fieldset class="space-y-12">
            <RevInputText
              id="first_name"
              v-model="values.firstName"
              :disabled
              :error="errors.firstName"
              :label="i18n(translations.firstName)"
            />
            <template v-if="isKanaNameEnabled">
              <RevInputText
                id="kana_first_name"
                v-model="values.kanaFirstName"
                :disabled
                :error="errors.kanaFirstName"
                :label="i18n(translations.kanaFirstName)"
              />
              <RevInputText
                id="latin_first_name"
                v-model="values.latinFirstName"
                :disabled
                :error="errors.latinFirstName"
                :label="i18n(translations.latinFirstName)"
              />
            </template>
            <RevInputText
              id="last_name"
              v-model="values.lastName"
              :disabled
              :error="errors.lastName"
              :label="i18n(translations.lastName)"
            />
            <template v-if="isKanaNameEnabled">
              <RevInputText
                id="kana_last_name"
                v-model="values.kanaLastName"
                :disabled
                :error="errors.kanaLastName"
                :label="i18n(translations.kanaLastName)"
              />
              <RevInputText
                id="latin_last_name"
                v-model="values.latinLastName"
                :disabled
                :error="errors.latinLastName"
                :label="i18n(translations.latinLastName)"
              />
            </template>
            <RevInputDate
              id="date_of_birth"
              v-model="values.dateOfBirth"
              :disabled
              :error="errors.dateOfBirth"
              :label="i18n(translations.dateOfBirth)"
            />
            <RevInputSelect
              id="country_of_citizenship"
              v-model="values.countryOfCitizenship"
              :disabled
              :error="errors.countryOfCitizenship"
              :label="i18n(translations.countryOfCitizenship)"
              :options="countries"
            />
            <RevInputSelect
              id="country_of_birth"
              v-model="values.countryOfBirth"
              :disabled
              :error="errors.countryOfBirth"
              :label="i18n(translations.countryOfBirth)"
              :options="countries"
            />
            <RevInputPhone
              id="phone_number"
              v-model="values.phoneNumber"
              class="sm:mt-8 sm:w-full md:mr-8 md:grow"
              :clear-button-aria-label="i18n(translations.clearPhoneNumber)"
              :default-country="currentMarket.countryCode"
              description=""
              :disabled
              :error="errors.phoneNumber"
              :label="i18n(translations.phoneNumber)"
              :translate="i18n.country"
            />
            <RevInputSelect
              id="country_of_residency"
              v-model="values.countryOfResidency"
              :disabled
              :error="errors.countryOfResidency"
              :label="i18n(translations.countryOfResidency)"
              :options="countries"
            />
          </fieldset>

          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.address) }}
            </legend>

            <AddressFields
              v-model:city="values.city"
              v-model:country="values.country"
              v-model:postal-code="values.postalCode"
              v-model:street="values.addressLine1"
              v-model:street2="values.addressLine2"
              class="space-y-12 space-y-reverse"
              :disabled
              :errors="{
                street: errors.addressLine1,
                city: errors.city,
                postalCode: errors.postalCode,
                country: errors.country,
                street2: errors.addressLine2,
              }"
              :feature-code
              :fields="{ firstName: false, lastName: false }"
              :optional-fields="['street2']"
            />
          </fieldset>

          <fieldset class="space-y-12">
            <legend class="body-1-bold mb-12">
              {{ i18n(translations.identityDocument) }}
            </legend>
            <div class="body-2 mb-12">
              {{ i18n(translations.photoIdDescription) }}
            </div>

            <RevInputSelect
              id="identification_type"
              v-model="values.identificationType"
              :disabled
              :error="errors.identificationType"
              :label="i18n(translations.identificationType)"
              :options="identificationTypes"
            />

            <div v-if="values.identificationType" class="space-y-12">
              <template
                v-if="
                  values.identificationType === IDENTIFICATION_TYPES.NATIONAL_ID
                "
              >
                <RevInputSelect
                  id="country_of_issue"
                  v-model="values.countryOfIssue"
                  :disabled
                  :error="errors.countryOfIssue"
                  :label="i18n(translations.countryOfIssue)"
                  :options="countries"
                />

                <div class="space-y-12">
                  <div class="body-1-bold">
                    {{ i18n(translations.uploadFrontSide) }}
                  </div>
                  <RevInputFileUpload
                    id="national_id_front_upload"
                    v-model="values.photoIdFront"
                    :accept="['.png', '.jpg', '.jpeg', '.pdf']"
                    :description="errors.photoIdFront"
                    :disabled
                    :errors="errors.photoIdFront as any"
                    :label-empty="i18n(translations.uploadFrontSide)"
                  />
                </div>

                <div class="space-y-12">
                  <div class="body-1-bold">
                    {{ i18n(translations.uploadBackSide) }}
                  </div>
                  <RevInputFileUpload
                    id="national_id_back_upload"
                    v-model="values.photoIdBack"
                    :accept="['.png', '.jpg', '.jpeg', '.pdf']"
                    :description="errors.photoIdBack"
                    :disabled
                    :errors="errors.photoIdBack as any"
                    :label-empty="i18n(translations.uploadBackSide)"
                  />
                </div>
              </template>

              <template
                v-else-if="
                  values.identificationType === IDENTIFICATION_TYPES.PASSPORT
                "
              >
                <RevInputSelect
                  id="country_of_issue"
                  v-model="values.countryOfIssue"
                  :disabled
                  :error="errors.countryOfIssue"
                  :label="i18n(translations.countryOfIssue)"
                  :options="countries"
                />

                <div class="space-y-12">
                  <div class="body-1-bold">
                    {{ i18n(translations.uploadPassportPhoto) }}
                  </div>
                  <RevInputFileUpload
                    id="passport_photo_upload"
                    v-model="values.photoIdFront"
                    :accept="['.png', '.jpg', '.jpeg', '.pdf']"
                    :description="errors.photoIdFront"
                    :disabled
                    :errors="errors.photoIdFront as any"
                    :label-empty="i18n(translations.uploadPassportPhoto)"
                  />
                </div>
              </template>
            </div>
          </fieldset>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { Country } from '@backmarket/http-api'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import {
  FORM_VALID,
  RevForm,
  type Validator,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInputDate } from '@ds/components/InputDate'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputPhone } from '@ds/components/InputPhone'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import {
  IDENTIFICATION_TYPES,
  VALIDATION_REGEX,
} from './StakeholderForm.constants'
import translations from './StakeholderForm.translations'
import type {
  StakeholderData,
  StakeholderFormValues,
} from './StakeholderForm.types'

const props = withDefaults(
  defineProps<{
    formId: string
    stakeholderData: StakeholderData | object
    featureCode: FeatureCode
    disabled?: boolean
  }>(),
  {
    disabled: false,
  },
)

const i18n = useI18n()
const { allMarkets, market: currentMarket } = useMarketplace()

const { isFeatureEnabled } = useBusinessFeaturesStore()
const isKanaNameEnabled = isFeatureEnabled('kanaNameEnabled')

const countries = computed(() =>
  Object.keys(allMarkets).map((countryCode) => ({
    label: i18n.country(countryCode),
    value: countryCode,
  })),
)

const identificationTypes = computed(() => [
  { label: i18n(translations.passport), value: IDENTIFICATION_TYPES.PASSPORT },
  {
    label: i18n(translations.nationalIdentityCard),
    value: IDENTIFICATION_TYPES.NATIONAL_ID,
  },
])

// Loading state is now managed by parent components

const stakeholder =
  props.stakeholderData && 'firstName' in props.stakeholderData
    ? props.stakeholderData
    : null

const initialFormValues: StakeholderFormValues = {
  firstName: stakeholder?.firstName ?? '',
  kanaFirstName: stakeholder?.kanaFirstName ?? '',
  latinFirstName: stakeholder?.latinFirstName ?? '',
  lastName: stakeholder?.lastName ?? '',
  kanaLastName: stakeholder?.kanaLastName ?? '',
  latinLastName: stakeholder?.latinLastName ?? '',
  dateOfBirth: stakeholder?.dateOfBirth ?? '',
  countryOfCitizenship: stakeholder?.countryOfCitizenship ?? '',
  phoneNumber: stakeholder?.phoneNumber ?? '',
  countryOfResidency: stakeholder?.countryOfResidency ?? '',
  countryOfBirth: stakeholder?.countryOfBirth ?? '',
  addressLine1: stakeholder?.address?.addressLine1 ?? '',
  addressLine2: stakeholder?.address?.addressLine2 ?? '',
  postalCode: stakeholder?.address?.postalCode ?? '',
  city: stakeholder?.address?.city ?? '',
  country: (stakeholder?.address?.country as Country | '') ?? '',
  identificationType:
    stakeholder?.identityDocument?.identificationType ??
    IDENTIFICATION_TYPES.PASSPORT,
  countryOfIssue: stakeholder?.identityDocument?.countryOfIssue ?? '',
  photoIdFront: [],
  photoIdBack: [],
}

function validate() {
  return makeValidate<StakeholderFormValues>({
    firstName: [required(i18n(translations.firstNameRequired))],
    lastName: [required(i18n(translations.lastNameRequired))],
    dateOfBirth: [required(i18n(translations.dateOfBirthRequired))],
    countryOfCitizenship: [
      required(i18n(translations.countryOfCitizenshipRequired)),
    ],
    phoneNumber: [
      required(i18n(translations.phoneNumberRequired)),
      matchingRegExp(
        VALIDATION_REGEX.PHONE_NUMBER,
        i18n(translations.phoneNumberInvalid),
      ),
    ],
    countryOfResidency: [
      required(i18n(translations.countryOfResidencyRequired)),
    ],
    addressLine1: [required(i18n(translations.addressLine1Required))],
    addressLine2: [],
    city: [required(i18n(translations.cityRequired))],
    postalCode: [required(i18n(translations.postalCodeRequired))],
    country: [required(i18n(translations.countryRequired))],
    identificationType: [
      required(i18n(translations.identificationTypeRequired)),
    ],
    countryOfIssue: [required(i18n(translations.countryOfIssueRequired))],
    photoIdFront: [required(i18n(translations.photoIdFrontRequired))],
    photoIdBack: [
      ((
        photoIdBack: StakeholderFormValues['photoIdBack'],
        formValues: StakeholderFormValues,
      ) => {
        if (
          formValues.identificationType === IDENTIFICATION_TYPES.NATIONAL_ID
        ) {
          return required(i18n(translations.photoIdBackRequired))(
            photoIdBack,
            formValues,
            {},
          )
        }

        return FORM_VALID
      }) as Validator<StakeholderFormValues>,
    ],
  })
}

// Form submission is now handled by parent components
</script>
